import type { MeetingSummary, RecordingSummary, RoomSettings } from '@live-stream/shared'
import type { HttpClient } from '../http.js'

/** A room in the signed-in person's list. */
export interface RoomSummary {
  id: string
  slug: string
  name: string
  createdAt: string
  endedAt: string | null
}

/** What anyone with the code may know about a room before joining it. */
export interface RoomInfo {
  room: {
    name: string
    lobbyEnabled: boolean
    /** The signed-in person owns (hosts) the room. */
    isOwner?: boolean
    /** Whether attendees may turn their mic and camera on now (a big meeting may not). */
    attendees?: { mic: boolean; camera: boolean }
    /** The words the note taker should know; only its host is told. */
    notetakerTerms?: string[]
  }
  /** The media server, when the server has one. */
  sfu: { url: string } | null
}

/** Signed links to a recording's files, good until `expiresAt`. */
export interface RecordingLinks {
  video: string | null
  poster: string | null
  tracks: Record<string, string | null>
  expiresAt: number
}

/** Rooms, and what each room keeps: its recordings and its meetings' transcripts. */
export class Rooms {
  constructor(private readonly http: HttpClient) {}

  /** The signed-in person's rooms. */
  async list(): Promise<RoomSummary[]> {
    return (await this.http.get<{ rooms: RoomSummary[] }>('/rooms')).rooms
  }

  async create(name: string, settings?: Partial<RoomSettings>): Promise<RoomSummary> {
    const body = { name, ...(settings ? { settings } : {}) }
    return (await this.http.post<{ room: RoomSummary }>('/rooms', body)).room
  }

  /** A room by its code; a 404 means there is no such code. */
  get(slug: string): Promise<RoomInfo> {
    return this.http.get<RoomInfo>(`/rooms/${slug}`)
  }

  /** Hosts: change some of the room's settings; the rest stay as they are. */
  async updateSettings(slug: string, settings: Partial<RoomSettings>): Promise<void> {
    await this.http.patch(`/rooms/${slug}`, { settings })
  }

  /** The room's recordings, newest first. */
  recordings(slug: string): Promise<{ room: { name: string }; recordings: RecordingSummary[] }> {
    return this.http.get(`/recordings/by-room/${slug}`)
  }

  /** The room's meetings that have a transcript. */
  async meetings(slug: string): Promise<MeetingSummary[]> {
    return (await this.http.get<{ meetings: MeetingSummary[] }>(`/transcripts/by-room/${slug}`))
      .meetings
  }

  /** One recording, with links to play and download it. */
  recording(id: string): Promise<{ recording: RecordingSummary; links: RecordingLinks }> {
    return this.http.get(`/recordings/${id}`)
  }

  async deleteRecording(id: string): Promise<void> {
    await this.http.delete(`/recordings/${id}`)
  }
}
